import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import {
  ContentBlock,
  DocumentItem,
  Page,
  PublicContent,
  Statistic,
} from '../types/content';
import {
  ContentQuery,
  fetchPublicContent,
  fetchPublicNewsList,
  fetchPublicServicesList,
  fetchPublicJobsList,
  fetchPublicCalculatorRates,
} from '../services/api';
import { useLanguage } from '../i18n/LanguageContext';
import {
  DEFAULT_BLOCKS,
  DEFAULT_CALCULATOR_RATES,
  DEFAULT_DOCUMENTS,
  DEFAULT_FOOTER_COLUMNS,
  DEFAULT_NEWS,
  DEFAULT_PAGES,
  DEFAULT_SERVICES,
  DEFAULT_SETTINGS,
  DEFAULT_STATISTICS,
} from '../data/defaults';

const DEFAULT_CONTENT: PublicContent = {
  settings: DEFAULT_SETTINGS,
  pages: DEFAULT_PAGES,
  news: DEFAULT_NEWS,
  services: DEFAULT_SERVICES,
  statistics: DEFAULT_STATISTICS,
  contentBlocks: DEFAULT_BLOCKS,
  documents: DEFAULT_DOCUMENTS,
  jobs: [],
  calculatorRates: DEFAULT_CALCULATOR_RATES,
};

const CAREERS_URL = 'https://kariyer.sanshine.com.tr/Liman/';

type SiteContentContextType = {
  content: PublicContent;
  isLoading: boolean;
  usingFallback: boolean;
  getPage: (slug: string) => Page | undefined;
  getBlocks: (pageSlug: string) => ContentBlock[];
  getBlock: (pageSlug: string, blockKey: string) => ContentBlock | undefined;
  getStats: (category: string) => Statistic[];
};

const SiteContentContext = createContext<SiteContentContextType | undefined>(
  undefined,
);

function hasItems<T>(arr?: T[]): arr is T[] {
  return Array.isArray(arr) && arr.length > 0;
}

function normalizeDocumentText(value?: string) {
  return (value || '')
    .toLocaleLowerCase('tr-TR')
    .replace(/ı/g, 'i')
    .replace(/İ/g, 'i')
    .replace(/ö/g, 'o')
    .replace(/ü/g, 'u')
    .replace(/ş/g, 's')
    .replace(/ç/g, 'c')
    .replace(/ğ/g, 'g')
    .replace(/\s+/g, ' ')
    .trim();
}

function documentGroup(doc: DocumentItem) {
  const title = normalizeDocumentText(doc.title);
  const category = normalizeDocumentText(doc.category);
  const contentType = normalizeDocumentText(doc.icon);

  if (
    category.includes('video') ||
    contentType.includes('video') ||
    title.endsWith('.mp4') ||
    title.includes('rezervasyon') ||
    title.includes('plaka')
  ) {
    return 'video';
  }
  if (category.includes('tarife') || title.includes('tarife')) return 'tarife';
  if (category.includes('sertifika') || title.includes('iso ')) return 'sertifika';
  if (category.includes('rehber') || title.includes('rehber')) return 'rehber';
  if (category.includes('kvkk') || category.includes('hukuki') || title.includes('kvkk')) return 'kvkk';
  return 'diger';
}

/**
 * Varsayilan dokuman kartlarinin hangi dosyayla eslesecegi. Dosyalar portalda dosya adiyla
 * yuklendigi icin eslesme dosya adi/basligi uzerinden yapilir. Ayni karta birden fazla dosya
 * uyarsa en son yuklenen (en buyuk id) kullanilir; boylece yeni surum yuklemek yeterlidir.
 */
const DOCUMENT_SLOT_MATCHERS: Record<string, (text: string) => boolean> = {
  'guncel konteyner tarifesi': (text) => /tarife/.test(text) && !/diger/.test(text),
  'diger tarifeler': (text) => /diger/.test(text) && /tarife/.test(text),
  'iso 9001 kalite yonetimi': (text) => /iso\s*9001/.test(text),
  'iso 14001 cevre yonetimi': (text) => /iso\s*14001/.test(text),
  'iso 45001 is sagligi & guvenligi': (text) => /iso\s*45001/.test(text),
  'iso 50001 enerji yonetimi': (text) => /iso\s*50001/.test(text),
  'tehlikeli yuk ellecleme rehberi': (text) => /tehlikeli/.test(text) && !/dangerous|ingilizce|english/.test(text),
  'tehlikeli yuk ellecleme rehberi (ingilizce)': (text) => /dangerous|tehlikeli.*(ingilizce|english)/.test(text),
  'entegre yonetim sistemi politikasi': (text) => /eys|entegre|politika/.test(text),
  'bluemed bulteni': (text) => /bluemed/.test(text),
  'kvkk aydinlatma metni': (text) => /kvkk|aydinlatma/.test(text),
  'arac rezervasyon kilavuzu': (text) => /rezervasyon|plaka|\.mp4$/.test(text),
};

function documentMatchText(doc: DocumentItem) {
  // "ISO_9001 1.pdf" -> "iso 9001 1.pdf"
  return normalizeDocumentText(doc.title).replace(/[_-]+/g, ' ');
}

function mergeDocuments(defaults = DEFAULT_DOCUMENTS, incoming?: PublicContent['documents']) {
  const apiDocs = [...(incoming || [])].sort((a, b) => a.sortOrder - b.sortOrder);
  if (apiDocs.length === 0) return defaults;

  const used = new Set<DocumentItem>();

  const merged = defaults
    .map((fallback): DocumentItem | null => {
      const matcher = DOCUMENT_SLOT_MATCHERS[normalizeDocumentText(fallback.title)];
      const available = apiDocs.filter((doc) => !used.has(doc));
      // Basligi birebir ayni olan dosya varsa o kullanilir; yoksa dosya adi kuraliyla eslenir
      const exact = available.filter(
        (doc) => normalizeDocumentText(doc.title) === normalizeDocumentText(fallback.title),
      );
      const candidates =
        exact.length > 0
          ? exact
          : available.filter((doc) => (matcher ? matcher(documentMatchText(doc)) : false));
      if (candidates.length === 0) return null;

      const matched = candidates.reduce((latest, doc) => ((doc.id ?? 0) > (latest.id ?? 0) ? doc : latest));
      candidates.forEach((doc) => used.add(doc));
      return {
        ...matched,
        // API'de title dosya adidir; indirme adinda kullanilmak uzere korunur
        fileName: matched.fileName || matched.title,
        title: fallback.title,
        description: fallback.description,
        category: fallback.category,
        // Dosya turu (indirme uzantisi) API dokumanindan korunur
        icon: matched.icon || fallback.icon,
        sortOrder: fallback.sortOrder,
      };
    })
    // API'de karsiligi olmayan varsayilan kart "#" linkiyle gosterilmez
    .filter((doc): doc is DocumentItem => Boolean(doc));

  const extras = apiDocs
    .filter((doc) => !used.has(doc) && documentGroup(doc) !== 'video')
    .map((doc, index) => ({ ...doc, sortOrder: defaults.length + index }));

  return [...merged, ...extras].sort((a, b) => a.sortOrder - b.sortOrder);
}

function normalizeServiceKey(value?: string) {
  return (value || '')
    .toLocaleLowerCase('tr-TR')
    .replace(/ı/g, 'i')
    .replace(/ö/g, 'o')
    .replace(/ü/g, 'u')
    .replace(/ş/g, 's')
    .replace(/ç/g, 'c')
    .replace(/ğ/g, 'g')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

function normalizeNavigation(
  navigation?: PublicContent['settings']['navigation'],
) {
  return (navigation || []).map((item) => {
    if (normalizeDocumentText(item.label) !== 'kariyer') return item;
    return { ...item, path: CAREERS_URL };
  });
}

function hasUsableImage(url?: string) {
  if (!url || !url.trim()) return false;
  const value = url.trim();
  if (/^data:/i.test(value) || /^https?:\/\//i.test(value)) return true;
  // Webpack/imported asset paths or absolute API paths
  if (value.startsWith('/') || value.startsWith('static/') || value.includes('/static/')) return true;
  // Bare filenames from portal uploads are not usable alone
  return value.includes('/');
}

function mergeServices(
  defaults = DEFAULT_SERVICES,
  incoming?: PublicContent['services'],
) {
  if (!incoming || incoming.length === 0) return defaults;

  // API'de hizmet varsa liste API'den kurulur; varsayilanlar sadece ayni slug'li kaydin eksik
  // alanlarini (gorsel, ozellik) tamamlar. Aksi halde API'de olmayan varsayilan hizmetler
  // (orn. "genel-kargo") sitede hayalet kart olarak kalir ve hizli linkler yanlis anchor'a gider.
  const merged = new Map<string, (typeof DEFAULT_SERVICES)[number]>();
  const defaultsByKey = new Map(
    defaults.map((service) => [normalizeServiceKey(service.slug || service.title), service]),
  );

  incoming.forEach((service, index) => {
    const key = normalizeServiceKey(service.slug || service.title) || `api-${service.id || index}`;
    const current = merged.get(key) || defaultsByKey.get(key);
    if (!current) {
      merged.set(key, {
        ...service,
        features: service.features || [],
        sortOrder: service.sortOrder ?? defaults.length + index,
        isPublished: service.isPublished !== false,
      });
      return;
    }

    merged.set(key, {
      ...current,
      ...service,
      id: service.id || current.id,
      title: service.title || current.title,
      slug: service.slug || current.slug,
      category: service.category || current.category,
      description: service.description || current.description,
      imageUrl: hasUsableImage(service.imageUrl) ? service.imageUrl : current.imageUrl,
      features:
        service.features && service.features.length > 0 ? service.features : current.features,
      sortOrder: service.sortOrder ?? current.sortOrder,
      isPublished: service.isPublished ?? current.isPublished,
    });
  });

  // Yayinda olmayanlar listede kalir (isPublished=false); sayfalar bunlari gizler.
  return Array.from(merged.values()).sort((a, b) => a.sortOrder - b.sortOrder);
}

function mergeCalculatorRates(
  defaults = DEFAULT_CALCULATOR_RATES,
  incoming?: PublicContent['calculatorRates'],
) {
  const merged = new Map<string, NonNullable<PublicContent['calculatorRates']>[number]>();

  defaults.forEach((rate) => {
    merged.set(rate.rateKey, rate);
  });

  (incoming || []).forEach((rate) => {
    if (rate.rateKey) merged.set(rate.rateKey, rate);
  });

  return Array.from(merged.values()).sort((a, b) => a.sortOrder - b.sortOrder);
}

function mergeFooterColumns(
  defaults = DEFAULT_FOOTER_COLUMNS,
  incoming?: PublicContent['settings']['footerColumns'],
) {
  if (!incoming || incoming.length === 0) return defaults;

  return defaults.map((fallback, index) => {
    const current = incoming[index];
    if (!current) return fallback;
    const links = (current.links || []).filter((link) => link.label || link.path);
    return {
      title: current.title || fallback.title,
      links: links.length > 0 ? links : fallback.links,
    };
  }).concat(
    incoming.slice(defaults.length).filter((column) => (column.links || []).some((link) => link.label || link.path)),
  );
}

export function SiteContentProvider({ children }: { children: ReactNode }) {
  const { apiLanguage } = useLanguage();
  const [content, setContent] = useState<PublicContent>(DEFAULT_CONTENT);
  const [isLoading, setIsLoading] = useState(true);
  const [usingFallback, setUsingFallback] = useState(true);

  useEffect(() => {
    // Dil degistiginde onceki istek hala yolda olabilir; eski yanit yeniyi ezmesin.
    let cancelled = false;
    const query: ContentQuery = { dil: apiLanguage };
    setIsLoading(true);

    Promise.allSettled([
      fetchPublicContent(query),
      fetchPublicNewsList(query),
      fetchPublicServicesList(query),
      fetchPublicJobsList(query),
      // Tarifeler dilden bagimsiz: her zaman TR kayitlarindan
      fetchPublicCalculatorRates(),
    ])
      .then((results) => {
        if (cancelled) return;
        const contentResult = results[0];
        const newsResult = results[1];
        const servicesResult = results[2];
        const jobsResult = results[3];
        const ratesResult = results[4];
        const data =
          contentResult.status === 'fulfilled' ? contentResult.value : DEFAULT_CONTENT;
        const apiNews =
          newsResult.status === 'fulfilled'
            ? newsResult.value
            : hasItems(data.news)
              ? data.news
              : DEFAULT_NEWS;
        const apiServices =
          servicesResult.status === 'fulfilled'
            ? servicesResult.value
            : hasItems(data.services)
              ? data.services
              : DEFAULT_SERVICES;
        const apiJobs =
          jobsResult.status === 'fulfilled'
            ? jobsResult.value
            : hasItems(data.jobs)
              ? data.jobs
              : [];

        setContent({
          settings: {
            ...DEFAULT_SETTINGS,
            ...data.settings,
            navigation: normalizeNavigation(data.settings?.navigation || DEFAULT_SETTINGS.navigation),
            footerColumns: mergeFooterColumns(DEFAULT_FOOTER_COLUMNS, data.settings?.footerColumns),
          },
          pages: hasItems(data.pages) ? data.pages : DEFAULT_PAGES,
          // API cevap verdiyse (bos olsa bile) gercek liste kullanilir; ornek haberler yalnizca
          // API'ye hic ulasilamazsa gosterilir. Aksi halde tum haberler silinince sahte haber cikar.
          news: newsResult.status === 'fulfilled' || hasItems(apiNews) ? apiNews : DEFAULT_NEWS,
          services: mergeServices(DEFAULT_SERVICES, apiServices),
          statistics: hasItems(data.statistics) ? data.statistics : DEFAULT_STATISTICS,
          contentBlocks: hasItems(data.contentBlocks) ? data.contentBlocks : DEFAULT_BLOCKS,
          jobs: apiJobs,
          // Portalda doküman kartı tanımlandıysa site yalnızca onları gösterir; hiç kart yoksa
          // yüklü dosyalar varsayılan kartlarla eşleştirilir (eski davranış).
          documents: hasItems(data.managedDocuments)
            ? data.managedDocuments
            : mergeDocuments(DEFAULT_DOCUMENTS, data.documents),
          calculatorRates: mergeCalculatorRates(
            DEFAULT_CALCULATOR_RATES,
            ratesResult.status === 'fulfilled' ? ratesResult.value : data.calculatorRates,
          ),
        });
        setUsingFallback(
          contentResult.status !== 'fulfilled' &&
            newsResult.status !== 'fulfilled' &&
            servicesResult.status !== 'fulfilled' &&
            jobsResult.status !== 'fulfilled',
        );
      })
      .catch(() => {
        if (cancelled) return;
        setContent(DEFAULT_CONTENT);
        setUsingFallback(true);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [apiLanguage]);

  const getPage = (slug: string) => content.pages.find((p) => p.slug === slug);

  const getBlocks = (pageSlug: string) =>
    content.contentBlocks
      .filter((b) => b.pageSlug === pageSlug)
      .sort((a, b) => a.sortOrder - b.sortOrder);

  const getBlock = (pageSlug: string, blockKey: string) =>
    content.contentBlocks.find(
      (b) => b.pageSlug === pageSlug && b.blockKey === blockKey,
    );

  const getStats = (category: string) =>
    content.statistics
      .filter((s) => s.category === category)
      .sort((a, b) => a.sortOrder - b.sortOrder);

  return (
    <SiteContentContext.Provider
      value={{ content, isLoading, usingFallback, getPage, getBlocks, getBlock, getStats }}
    >
      {children}
    </SiteContentContext.Provider>
  );
}

export function useSiteContent() {
  const ctx = useContext(SiteContentContext);
  if (!ctx) throw new Error('useSiteContent SiteContentProvider icinde kullanilmali');
  return ctx;
}

/** "Metin|deger" formatli item listelerini ayristirir */
export function splitItem(raw: unknown): string[] {
  if (typeof raw === 'string') return raw.split('|');
  return [];
}

export function stringItems(items?: ContentBlock['items']): string[] {
  if (!items) return [];
  return (items as unknown[]).filter(
    (i): i is string => typeof i === 'string',
  );
}
